import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import SessionWarning from "@/components/SessionWarning";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = logged in
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("trw_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("trw_token");
        setUser(false);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    localStorage.setItem("trw_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("trw_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const setSession = (data) => {
    localStorage.setItem("trw_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("trw_token");
    setUser(false);
  };

  // auto-logout after 60 minutes of inactivity, with a 60-second warning
  const [warning, setWarning] = useState(false);
  const resetRef = useRef(null);
  useEffect(() => {
    if (!user) { setWarning(false); return; }
    let warnTimer, logoutTimer;
    const reset = () => {
      clearTimeout(warnTimer);
      clearTimeout(logoutTimer);
      setWarning(false);
      warnTimer = setTimeout(() => setWarning(true), 59 * 60 * 1000);
      logoutTimer = setTimeout(() => {
        setWarning(false);
        localStorage.removeItem("trw_token");
        setUser(false);
      }, 60 * 60 * 1000);
    };
    resetRef.current = reset;
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(warnTimer); clearTimeout(logoutTimer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, setSession, logout }}>
      {children}
      <SessionWarning open={!!user && warning} seconds={60} onStay={() => resetRef.current?.()} />
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
